/**
 * theme.ts — the tech app's light / dark switch (MOBILE-LOOK-01 "Glass",
 * Seth 2026-09-22). LIGHT by default (Seth, after seeing dark first because
 * his PC runs dark: "I don't like how the dark mode looks compared to the
 * light view" — and driveway sunlight wants light anyway). A tech can pick
 * Dark, or Device (follow the iPad's own setting), under More → Appearance.
 * The choice lives on the device (localStorage) — a comfort setting, not data.
 *
 * The root `[data-tech-app]` element carries data-theme="light" | "dark";
 * every token in index.css keys off that. The native shell's status bar
 * follows along (setNativeTheme).
 */
import { useCallback, useEffect, useState } from "react";
import { setNativeTheme } from "@/native/boot";

export type ThemePref = "system" | "light" | "dark";
const KEY = "pegasus.m.theme";
const DARK_MQ = "(prefers-color-scheme: dark)";

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === "system" || v === "dark" ? v : "light";
  } catch {
    return "light";
  }
}

export function useTechTheme(): { pref: ThemePref; setPref: (p: ThemePref) => void; dark: boolean } {
  const [pref, setPrefState] = useState<ThemePref>(readPref);
  const [systemDark, setSystemDark] = useState<boolean>(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(DARK_MQ).matches : false,
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(DARK_MQ);
    const on = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const setPref = useCallback((p: ThemePref) => {
    setPrefState(p);
    try {
      if (p === "light") localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, p);
    } catch { /* fine */ }
  }, []);
  const dark = pref === "dark" || (pref === "system" && systemDark);
  useEffect(() => { setNativeTheme(dark); }, [dark]);
  return { pref, setPref, dark };
}
